import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, ExternalLink, Loader2, Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
// The PDF.js worker, bundled by Vite as an asset URL (the modern, CSP-safe
// pattern — no CDN, no eval/unsafe-eval, works offline). Verified against
// pdfjs-dist v6: the worker ships at build/pdf.worker.min.mjs.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 1.2;

/**
 * Horizontal padding around the canvas, in CSS px — the `p-3` on the wrapper
 * below, both sides.
 *
 * A named constant because fit-to-width has to SUBTRACT it. containerWidth is
 * the scroll container's clientWidth, which includes this padding, so a fit
 * scale computed from it made the canvas 24px wider than the box it sits in.
 * The overflow was invisible only because the canvas also carried `max-w-full`,
 * which quietly squashed it back — see the render below.
 */
const CANVAS_PADDING_X = 24;

/**
 * A cross-browser PDF viewer that renders a document to a <canvas> via PDF.js
 * (pdfjs-dist) — no dependency on a native browser PDF plugin, so it composites
 * and screenshots identically in every browser. Fit-width by default, with page
 * navigation, zoom, HiDPI-crisp rendering, vertical scroll for tall pages, and
 * graceful loading / error states (the error state keeps the pop-out fallback).
 *
 * `zoom` is a multiplier on the fit-to-width scale: 1 = fit width, >1 zoomed in.
 */
export function PdfDocumentViewer({
  url,
  title,
  popOutHref,
  onPopOut,
}: {
  url: string;
  title: string;
  /** Raw new-tab fallback for the error state. Optional — omit inside the pop-out
   *  window itself (no self-pop-out). */
  popOutHref?: string;
  /** Preferred pop-out: opens the styled document window. When set, the error
   *  state offers it instead of the raw new-tab link. */
  onPopOut?: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  // Which render is the current one.
  //
  // renderPage awaits twice, and React can start a second call during either
  // gap — a resize alone does it, because the vertical scrollbar appearing
  // changes clientWidth the instant the first page is laid out. Without a
  // generation the superseded call carries on: it clears the canvas (assigning
  // canvas.width does that), paints over the newer render, and PDF.js throws
  // "Cannot use the same canvas during multiple render operations" for whichever
  // loses. The old bare catch swallowed that, so the result was a white page
  // with status still 'ready' and nothing left to trigger a repaint.
  const renderGenRef = useRef(0);

  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  // Load the document from the asset URL. getDocument spins up the worker; the
  // task is destroyed on unmount / url change so we never leak a worker.
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setDoc(null);
    setPageNum(1);
    const task = pdfjs.getDocument({
      url,
      // Non-embedded standard-14 fonts (Helvetica/Courier/…) load from the
      // bundled Foxit substitutes, wired as a served asset path (see below).
      standardFontDataUrl: STANDARD_FONT_URL,
      // The image decoders. Without this, a scanned page decodes to nothing and
      // still reports success — see the note on WASM_URL below.
      wasmUrl: WASM_URL,
    });
    task.promise.then(
      (loaded) => {
        // If the component unmounted / url changed first, the cleanup below has
        // already called task.destroy(), which tears the document down too.
        if (cancelled) return;
        setDoc(loaded);
        setNumPages(loaded.numPages);
        setStatus('ready');
      },
      (err: unknown) => {
        if (cancelled) return;
        // Never surface document contents in an error message (PHI hygiene).
        console.error('PDF load failed', err);
        setStatus('error');
      },
    );
    return () => {
      cancelled = true;
      void task.destroy();
    };
  }, [url]);

  // Track the scroll container's width so fit-to-width recomputes on resize.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [status]);

  // Render the current page whenever the page, zoom, container width, or doc
  // changes. Cancels any in-flight render first (a canvas can only host one
  // render at a time). HiDPI-crisp via devicePixelRatio.
  const renderPage = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!doc || !canvas || containerWidth <= 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return; // jsdom / headless without a 2d context — nothing to paint.

    // Claim this render before the first await, so a later call can see that
    // it has superseded us even while we are still suspended.
    const gen = ++renderGenRef.current;
    renderTaskRef.current?.cancel();

    let page;
    try {
      page = await doc.getPage(pageNum);
    } catch {
      return;
    }
    // Superseded while fetching the page: do nothing at all. In particular do
    // NOT touch the canvas — the newer render may already have painted it.
    if (gen !== renderGenRef.current) return;
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const base = page.getViewport({ scale: 1 });
    // Fit the page to the pane's CONTENT width, then apply the zoom multiplier.
    //
    // The padding is subtracted because containerWidth is the scroll
    // container's clientWidth. Without it, "fit to width" produced a canvas
    // wider than the space it had, which `max-w-full` then squashed back —
    // making fit-width silently the wrong scale and every zoom level a
    // horizontal squash against a vertical stretch.
    const fitWidthPx = Math.max(1, containerWidth - CANVAS_PADDING_X);
    const fitScale = fitWidthPx / base.width;
    const cssScale = fitScale * zoom;
    const viewport = page.getViewport({ scale: cssScale * dpr });

    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
    canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;

    const task = page.render({ canvas, canvasContext: ctx, viewport });
    renderTaskRef.current = task;
    try {
      await task.promise;
    } catch (err: unknown) {
      // A cancellation is expected and means a newer render is on its way.
      // Anything else is a REAL failure — a corrupt page, an image format the
      // build cannot decode — and used to be swallowed here, leaving a blank
      // canvas that still reported itself ready. A viewer that cannot say it
      // failed is worse than one that fails: the reviewer reads white as "this
      // record is empty" and judges the case against nothing.
      if (err instanceof pdfjs.RenderingCancelledException) return;
      if (gen !== renderGenRef.current) return; // superseded; not our failure
      // No page contents in the message (PHI hygiene) — the exception text
      // describes the operation, not the document.
      console.error('PDF page render failed', err);
      setStatus('error');
    }
  }, [doc, pageNum, zoom, containerWidth]);

  useEffect(() => {
    void renderPage();
  }, [renderPage]);

  useEffect(() => () => renderTaskRef.current?.cancel(), []);

  const goPrev = () => setPageNum((n) => Math.max(1, n - 1));
  const goNext = () => setPageNum((n) => Math.min(numPages, n + 1));
  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, +(z * ZOOM_STEP).toFixed(3)));
  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, +(z / ZOOM_STEP).toFixed(3)));
  const fitWidth = () => setZoom(1);

  const btn =
    'inline-flex size-7 items-center justify-center rounded-coronis text-muted-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Viewer toolbar: page navigation · zoom. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-border bg-card px-2 py-1.5">
        <div className="flex items-center gap-1">
          <button type="button" className={btn} onClick={goPrev} disabled={status !== 'ready' || pageNum <= 1} aria-label="Previous page" title="Previous page">
            <ZoomInChevron dir="left" />
          </button>
          <span className="min-w-[84px] text-center text-[11.5px] tabular-nums text-muted-foreground">
            {status === 'ready' ? `Page ${pageNum} of ${numPages}` : 'Page — of —'}
          </span>
          <button type="button" className={btn} onClick={goNext} disabled={status !== 'ready' || pageNum >= numPages} aria-label="Next page" title="Next page">
            <ZoomInChevron dir="right" />
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" className={btn} onClick={zoomOut} disabled={status !== 'ready' || zoom <= MIN_ZOOM} aria-label="Zoom out" title="Zoom out">
            <ZoomOut className="size-3.5" />
          </button>
          <span className="min-w-[44px] text-center text-[11.5px] tabular-nums text-muted-foreground" aria-live="polite">
            {Math.round(zoom * 100)}%
          </span>
          <button type="button" className={btn} onClick={zoomIn} disabled={status !== 'ready' || zoom >= MAX_ZOOM} aria-label="Zoom in" title="Zoom in">
            <ZoomIn className="size-3.5" />
          </button>
          <button type="button" className={btn} onClick={fitWidth} disabled={status !== 'ready'} aria-label="Fit to width" title="Fit to width">
            <Maximize2 className="size-3.5" />
          </button>
        </div>
      </div>

      {/* Canvas scroll area — fills the pane, scrolls for a tall / zoomed page. */}
      <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-auto bg-muted/40" data-testid="pdf-scroll">
        {status === 'loading' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground" aria-busy="true">
            <Loader2 className="size-5 animate-spin" />
            <span className="text-[12px]">Loading document…</span>
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <AlertTriangle className="size-6 text-coronis-amber-500" />
            <span className="text-[12.5px] font-medium text-foreground">This document couldn’t be displayed here.</span>
            {onPopOut ? (
              <button
                type="button"
                onClick={onPopOut}
                className="inline-flex items-center gap-1.5 rounded-coronis border border-border bg-card px-2.5 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
              >
                <ExternalLink className="size-3.5" />
                Open in a separate window
              </button>
            ) : popOutHref ? (
              <a
                href={popOutHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-coronis border border-border bg-card px-2.5 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
              >
                <ExternalLink className="size-3.5" />
                Open in a new tab
              </a>
            ) : null}
          </div>
        )}
        {/* `w-fit min-w-full` so the row is at least the pane's width (centring
            works when the page is narrower than the pane) and GROWS to the
            canvas when zoomed in, so the scroll container can reach both edges.
            A plain `justify-center` row centres an over-wide child and puts its
            left edge permanently out of reach. */}
        <div className="flex w-fit min-w-full justify-center p-3">
          <canvas
            ref={canvasRef}
            aria-label={title}
            // NO max-w-full. The render sets canvas.style.width to the zoomed
            // width and canvas.style.height to the zoomed height; a CSS cap on
            // the width alone constrained one axis and not the other, so every
            // zoom above fit-width rendered the page horizontally squashed —
            // "the zoom does not override fit to width, the pdf gets
            // stretched". Overflow is what the scroll container is for.
            className={
              status === 'ready'
                ? 'rounded-coronis shadow-sm ring-1 ring-border'
                : 'sr-only'
            }
          />
        </div>
      </div>
    </div>
  );
}

/** A small chevron for page nav (kept local so the toolbar reads as one unit). */
function ZoomInChevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {dir === 'left' ? <path d="m15 18-6-6 6-6" /> : <path d="m9 18 6-6-6-6" />}
    </svg>
  );
}

/** BASE_URL, which is '/workbench/' here. '/' under a bare test runner. */
const ASSET_BASE =
  typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL ? import.meta.env.BASE_URL : '/';

// Non-embedded standard-14 fonts render from PDF.js's bundled Foxit substitutes.
// Vite copies pdfjs-dist/standard_fonts into the app's public dir at build time
// (see vite.config.ts) so they're served at BASE_URL + 'pdfjs/standard_fonts/'
// — self-hosted, no CDN.
const STANDARD_FONT_URL = ASSET_BASE + 'pdfjs/standard_fonts/';

/**
 * Where PDF.js fetches its image-decoder WebAssembly from.
 *
 * # The failure this exists to end
 *
 * PDF.js 6 decodes CCITT Fax, JBIG2 and JPEG2000 images in WebAssembly, not
 * JavaScript, and fetches `jbig2.wasm` / `openjpeg.wasm` from this URL the
 * first time a page needs one. Those three codecs are what document-imaging
 * systems produce: an FA record is a scanned bitonal page, which is CCITT G4.
 *
 * The option defaults to null. Left unset, PDF.js cannot load the decoder and
 * — this is the part that made it invisible — does not fail. It warns ("Unable
 * to decode image", "Dependent image isn't ready yet"), skips the image, and
 * RESOLVES the render promise. Status stays 'ready', the error state above
 * never fires, and the reviewer gets a white page with a correct page count:
 * a scanned record presented as an empty one, which is the single worst thing
 * this pane can do. It is also why the earlier "a page that fails to render no
 * longer shows as blank" fix did not catch it — that guards a REJECTED render,
 * and this path succeeds.
 *
 * Measured on the same CCITT-G4 page, pdfjs-dist 6.1.200, Chrome:
 *   wasmUrl unset -> render resolved, 0 of 124,848 px dark  (blank)
 *   wasmUrl set   -> render resolved, 18,891 px dark (15.1%)
 * against 14.9% from an independent decoder on the same file.
 *
 * Trailing slash is required: PDF.js concatenates the bare filename onto it.
 */
const WASM_URL = ASSET_BASE + 'pdfjs/wasm/';
